import { motion, AnimatePresence } from 'framer-motion';
import { Chip } from '@heroui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function FAQ() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number>(0);
  const faqs = t('home.faq.items', { returnObjects: true }) as Array<{
    question: string;
    answer: string;
  }>;

  return (
    <section className="relative overflow-hidden bg-[#0D0D0D] py-24">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-8%] top-12 h-80 w-80 rounded-full bg-brand-green/10 blur-3xl" />
        <div className="absolute right-[-10%] bottom-[-12%] h-[26rem] w-[26rem] rounded-full bg-brand-ocean/10 blur-3xl" />
        <div className="absolute left-[28%] top-[18%] h-64 w-64 rounded-full bg-white/5 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-4xl px-6">
        <div className="mb-12 text-center md:mb-14">
          <Chip
            variant="flat"
            className="mb-4 border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-brand-green"
            startContent={
              <span className="mr-2 inline-flex items-center">
                <i className="ri-questionnaire-line text-base" />
              </span>
            }
          >
            {t('home.faq.badge')}
          </Chip>
          <h2 className="mb-5 text-4xl font-caprasimo text-white md:text-5xl">
            {t('home.faq.title')}
          </h2>
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-white/68 md:text-lg">
            {t('home.faq.description')}
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={faq.question}
                className="group"
              >
                <div
                  className={`overflow-hidden rounded-[18px] border backdrop-blur-sm transition-all duration-300 ${
                    isOpen
                      ? 'border-white/10 bg-[rgba(22,22,22,0.96)] shadow-[0_18px_40px_rgba(0,0,0,0.3)]'
                      : 'border-white/10 bg-[rgba(17,17,17,0.84)] shadow-[0_12px_28px_rgba(0,0,0,0.18)] hover:bg-[rgba(21,21,21,0.9)]'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? -1 : index)}
                    className="flex w-full items-center justify-between gap-5 px-5 py-4 text-left md:px-6 md:py-4.5"
                    aria-expanded={isOpen}
                  >
                    <div className="pr-4">
                      <h3 className="text-base font-semibold leading-snug text-white/92 md:text-[1.02rem]">
                        {faq.question}
                      </h3>
                    </div>

                    <div
                      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                        isOpen
                          ? 'border-white/10 bg-white/8 text-white/78'
                          : 'border-white/10 bg-white/4 text-white/46 group-hover:border-white/10 group-hover:text-white/72'
                      }`}
                    >
                      <i
                        className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${
                          isOpen ? 'rotate-180' : 'rotate-0'
                        }`}
                      />
                    </div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen ? (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-white/10 px-5 pb-5 pt-4 md:px-6">
                          <p className="max-w-3xl text-[15px] leading-7 text-white/62 md:text-[15px]">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
